import api from './api';
import type { MenuItem, MenuItemKind, MenuVisibility, PaginatedResponse } from '../types/menu';

const base = (venueId: string, menuId: string) =>
  `/admin/venue/${venueId}/menu/${menuId}/items`;

const menuItemService = {
  /** List items on a menu, paginated. Filterable by kind/visibility/sectionId. */
  getItems: async (
    venueId: string,
    menuId: string,
    page = 1,
    pageSize = 50,
    filters?: { kind?: MenuItemKind; visibility?: MenuVisibility; sectionId?: string | 'unsectioned' },
  ): Promise<PaginatedResponse<MenuItem>> => {
    const params: Record<string, unknown> = { page, pageSize };
    if (filters?.kind) params.kind = filters.kind;
    if (filters?.visibility) params.visibility = filters.visibility;
    if (filters?.sectionId) params.sectionId = filters.sectionId;
    const response = await api.get(base(venueId, menuId), { params });
    return response.data;
  },

  /** Get a single item. */
  getItem: async (venueId: string, menuId: string, itemId: string): Promise<MenuItem> => {
    const response = await api.get(`${base(venueId, menuId)}/${itemId}`);
    return response.data;
  },

  /**
   * Create an item. Phase 2A backend enforces kind === 'COCKTAIL'; this
   * service does not constrain the type here so the form layer can apply its
   * own restriction.
   */
  createItem: async (
    venueId: string,
    menuId: string,
    data: Omit<MenuItem, '_id' | 'venueId' | 'menuId' | 'active' | 'createdAt' | 'updatedAt'>,
  ): Promise<MenuItem> => {
    const response = await api.post(base(venueId, menuId), data);
    return response.data;
  },

  /** Update an item. */
  updateItem: async (
    venueId: string,
    menuId: string,
    itemId: string,
    data: Partial<Omit<MenuItem, '_id' | 'venueId' | 'menuId' | 'createdAt' | 'updatedAt'>>,
  ): Promise<MenuItem> => {
    const response = await api.put(`${base(venueId, menuId)}/${itemId}`, data);
    return response.data;
  },

  /** Hard delete an item. */
  deleteItem: async (venueId: string, menuId: string, itemId: string): Promise<void> => {
    await api.delete(`${base(venueId, menuId)}/${itemId}`);
  },
};

export default menuItemService;
